import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RcFile } from 'antd/es/upload';

import './index.less';

import {
  MessageRole,
  AgentMessageModel,
  AgentModel,
  AgentMessageEventType,
  SessionModel,
  DataInterfaceInput,
  ChatMessageContentInner,
  DataPayload,
  SessionProviderModel,
  AgentResourceModel,
  AgentMessageEventToolCallDataOutput,
  AgentRunModel,
} from '../../../gamma-api';
import { IMessageListItem, MessageListItemDirection } from '../MessageListItem';
import { getChatMessageTextContent } from '../../utils/messageUtil';
import MessageList from '../MessageList';
import IbTypography from '../common/IbTypography';
import AgentMessageToolsModal from '../AgentMessageToolsModal';
import IbButton from '../common/IbButton';
import IbIcon from '../common/IbIcon';
import ContextWindowIndicator, { IContextWindowUsage } from '../ContextWindowIndicator';

import { StagedAttachment } from './types';
import MessageInputAdditionalControls from './MessageInputAdditionalControls';
import MessageInputAttachments from './MessageInputAttachments';
import MessageResources from './MessageResources';
import MessageAttachments from './MessageAttachments';
import MessageEvents from './MessageEvents';
import ToolCallEvent from './ToolCallEvent';
import RuntimeErrorEventDetails from './RuntimeErrorEventDetails';
import RuntimeErrorEvent from './RuntimeErrorEvent';
import HandoffEvent from './HandoffEvent';
import HandoffEventDetails from './HandoffEventDetails';
import ToolCallEventDetails from './ToolCallEventDetails';
import GuardrailChecksEventDetails from './GuardrailChecksEventDetails';
import GuardrailEventsTimeline from './GuardrailEventsTimeline';
import { GuardrailDetailsTarget } from './guardrailEventGroups';
import UsageTokens from './UsageTokens';
import ChatCompressionEvent from './ChatCompressionEvent';
import ChatCompressionEventDetails from './ChatCompressionEventDetails';

const MAIN_CLASS_NAME = 'agent-testing-chat';
const CONTENT_CLASS_NAME = `${MAIN_CLASS_NAME}__content`;
const DIALOG_CONTENT_CLASS_NAME = `${CONTENT_CLASS_NAME}__dialog`;
const MESSAGE_CLASS_NAME = `${DIALOG_CONTENT_CLASS_NAME}__message`;
const MESSAGE_CONTENT_CLASS_NAME = `${MESSAGE_CLASS_NAME}__content`;
const MESSAGE_CONTENT_SUFFIX_CLASS_NAME = `${MESSAGE_CONTENT_CLASS_NAME}__suffix`;
const MESSAGE_TITLE_CLASS_NAME = `${MESSAGE_CLASS_NAME}__title`;
const MESSAGE_TITLE_SUFFIX_CLASS_NAME = `${MESSAGE_TITLE_CLASS_NAME}__suffix`;
const INPUT_PREFIX_CLASS_NAME = `${DIALOG_CONTENT_CLASS_NAME}__input-prefix`;
const CONTENT_DETAILS_CLASS_NAME = `${CONTENT_CLASS_NAME}__details`;
const CONTENT_DETAILS_EMPTY_CLASS_NAME = `${CONTENT_DETAILS_CLASS_NAME}_empty`;

const USER_AVATAR_NAME_DEFAULT = 'user';
const AGENT_AVATAR_NAME_DEFAULT = 'default';

interface IAgentTestingChatProps {
  agent?: AgentModel;
  authSession?: SessionModel | null;
  messageList: AgentMessageModel[];
  attachments?: StagedAttachment[];
  resourceList?: AgentResourceModel[];
  dataInterface?: DataInterfaceInput;
  codeFolder?: string;
  contextWindow?: IContextWindowUsage;
  loading?: boolean;
  messageProcessing?: boolean;
  readonly?: boolean;
  onMessageSelect?: (message: IMessageListItem) => void;
  onMessageSend?: (content: ChatMessageContentInner, payload: DataPayload) => void;
  onRunRetry?: (run: AgentRunModel) => void;
  onResourceAdd?: (resource: AgentResourceModel) => void;
  onResourceDelete?: (resource: AgentResourceModel) => void;
  onAttachmentsUpload?: (files: RcFile[]) => void;
  onAttachmentDelete?: (attachment: StagedAttachment) => void;
  onAttachmentReUpload?: (attachment: StagedAttachment) => void;
  onSignIn?: (provider: SessionProviderModel) => void;
  onSignInCancel?: (provider: SessionProviderModel) => void;
  onSignOut?: () => void;
  onClean?: () => void;
}

const AgentTestingChat: React.FC<IAgentTestingChatProps> = ({
  agent,
  authSession,
  messageList,
  attachments = [],
  resourceList = [],
  dataInterface,
  codeFolder,
  contextWindow,
  loading,
  messageProcessing,
  readonly,
  onMessageSelect,
  onMessageSend,
  onRunRetry,
  onResourceAdd,
  onResourceDelete,
  onAttachmentsUpload,
  onAttachmentReUpload,
  onAttachmentDelete,
  onSignIn,
  onSignInCancel,
  onSignOut,
  onClean,
}) => {
  const { t } = useTranslation();

  const [selectedMessageId, setSelectedMessageId] = useState<string>();
  const [selectedMessageEventId, setSelectedMessageEventId] = useState<string>();
  const [guardrailDetailsTarget, setGuardrailDetailsTarget] = useState<GuardrailDetailsTarget>();

  const selectedMessage = messageList.find((m) => m.message_id === selectedMessageId);
  const selectedMessageEvent = (selectedMessage?.events || []).find((e) => e.id === selectedMessageEventId);

  const [toolsModalVisible, setToolsModalVisible] = useState(false);

  const mapMessage = (message: AgentMessageModel): IMessageListItem => {
    const direction =
      message.chat_message.role === MessageRole.User
        ? MessageListItemDirection.OUTBOUND
        : MessageListItemDirection.INBOUND;
    const isOutbound = direction === MessageListItemDirection.OUTBOUND;
    const hasFinalAssistantMessageEvent = message.events.some(
      (event) => event.data.event_type === AgentMessageEventType.AssistantMessage && event.data.is_final
    );

    const onEventSelect = (eventId: string) => {
      setSelectedMessageId(message.message_id);
      setSelectedMessageEventId(eventId);
      setGuardrailDetailsTarget(undefined);
    };

    const onToolsSelect = (message: AgentMessageModel) => () => {
      setSelectedMessageId(message.message_id);
      setToolsModalVisible(true);
    };

    const getSenderName = () =>
      isOutbound ? t('Agent: Testing chat: Sender name') : agent?.settings?.widget?.display_name || '';

    const getSenderAvatar = () =>
      isOutbound ? USER_AVATAR_NAME_DEFAULT : agent?.settings?.widget?.avatar?.name || AGENT_AVATAR_NAME_DEFAULT;

    const renderContentPrefix = () => (
      <MessageEvents
        message={message}
        selectedMessageEvent={selectedMessageEvent}
        selectedMessageId={selectedMessageId}
        onEventSelect={onEventSelect}
      />
    );

    const renderContentSuffix = () => {
      const renderedResources = <MessageResources resources={message.resources} />;
      const renderedAttachments = <MessageAttachments message={message} />;
      if (!renderedResources && !renderedAttachments) {
        return null;
      }
      return (
        <div className={MESSAGE_CONTENT_SUFFIX_CLASS_NAME}>
          {renderedResources}
          {renderedAttachments}
        </div>
      );
    };

    const renderTitleSuffix = () => {
      if (!isOutbound) {
        return (
          <div className={MESSAGE_TITLE_SUFFIX_CLASS_NAME}>
            <IbButton icon={<IbIcon iconName="toolkit" />} type="icon-bordered" onClick={onToolsSelect(message)} />
          </div>
        );
      }

      return null;
    };

    const renderFooterSuffix = () => {
      if (message.chat_message.role !== MessageRole.Assistant) {
        return null;
      }

      const isLastMessage = message.message_id === messageList[messageList.length - 1]?.message_id;
      if (messageProcessing && isLastMessage) {
        return null;
      }

      const generations = message.stats?.usage;
      if (!generations || generations.total === 0) {
        return null;
      }

      return <UsageTokens generations={generations} />;
    };

    return {
      id: message.message_id,
      senderName: getSenderName(),
      senderAvatar: getSenderAvatar(),
      content: hasFinalAssistantMessageEvent ? '' : getChatMessageTextContent(message.chat_message, message.payloads),
      isSelected: false,
      direction,
      contentPrefix: renderContentPrefix(),
      contentSuffix: renderContentSuffix(),
      titleSuffix: renderTitleSuffix(),
      footerSuffix: renderFooterSuffix(),
    };
  };

  const renderSelectedMessageEventDetails = () => {
    const detailsContentClasses = [CONTENT_DETAILS_CLASS_NAME];

    if (!selectedMessageEvent) {
      detailsContentClasses.push(CONTENT_DETAILS_EMPTY_CLASS_NAME);
      return (
        <div className={detailsContentClasses.join(' ')}>
          <IbTypography.Paragraph disabled type="secondary">
            {t('Agent: Testing chat: Details content: Empty placeholder')}
          </IbTypography.Paragraph>
        </div>
      );
    }

    const getToolSchema = () => {
      const toolCallData = selectedMessageEvent.data as AgentMessageEventToolCallDataOutput;
      return selectedMessage?.tools.flatMap((mt) => mt.tools).find((tool) => tool.name === toolCallData.name);
    };

    return (
      <div className={detailsContentClasses.join(' ')}>
        {selectedMessageEvent?.data.event_type === AgentMessageEventType.ToolCall && (
          <>
            <ToolCallEvent isSelected toolCallData={selectedMessageEvent.data} toolSchema={getToolSchema()} />
            <ToolCallEventDetails toolCallData={selectedMessageEvent.data} />
          </>
        )}
        {selectedMessageEvent?.data.event_type === AgentMessageEventType.RuntimeError && (
          <>
            <RuntimeErrorEvent isSelected />
            <RuntimeErrorEventDetails runtimeErrorData={selectedMessageEvent.data} />
          </>
        )}
        {selectedMessageEvent?.data.event_type === AgentMessageEventType.Handoff && (
          <>
            <HandoffEvent isSelected handoffData={selectedMessageEvent.data} />
            <HandoffEventDetails handoffData={selectedMessageEvent.data} />
          </>
        )}
        {selectedMessageEvent?.data.event_type === AgentMessageEventType.Guardrail && (
          <>
            <GuardrailEventsTimeline
              events={selectedMessage?.events || []}
              responsePending={
                messageProcessing && selectedMessage?.run_id === messageList[messageList.length - 1]?.run_id
              }
              onDetailsSelect={setGuardrailDetailsTarget}
            />
            <GuardrailChecksEventDetails
              events={selectedMessage?.events || []}
              navigationTarget={guardrailDetailsTarget}
            />
          </>
        )}
        {selectedMessageEvent?.data.event_type === AgentMessageEventType.ChatCompression && (
          <>
            <ChatCompressionEvent isSelected chatCompressionData={selectedMessageEvent.data} />
            <ChatCompressionEventDetails chatCompressionData={selectedMessageEvent.data} />
          </>
        )}
      </div>
    );
  };

  const renderInputEditorPrefix = () => {
    if (!attachments.length && !resourceList.length) {
      return null;
    }

    return (
      <div className={INPUT_PREFIX_CLASS_NAME}>
        <MessageResources resources={resourceList} onDelete={onResourceDelete} />
        <MessageInputAttachments
          attachments={attachments}
          onAttachmentDelete={onAttachmentDelete}
          onAttachmentReUpload={onAttachmentReUpload}
        />
      </div>
    );
  };

  const onToolsModalCancel = () => setToolsModalVisible(false);

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={CONTENT_CLASS_NAME}>
        <div className={DIALOG_CONTENT_CLASS_NAME}>
          <MessageList
            agentId={agent?.agent_id}
            authSession={authSession}
            codeFolder={codeFolder}
            dataInterface={dataInterface}
            inputEditorPrefix={renderInputEditorPrefix()}
            inputEditorSuffix={
              <MessageInputAdditionalControls onAttachmentsUpload={onAttachmentsUpload} onResourceAdd={onResourceAdd} />
            }
            loading={loading}
            messageList={messageList.map(mapMessage)}
            messageProcessing={messageProcessing}
            readonly={readonly}
            sendControlsPrefix={<ContextWindowIndicator contextWindow={contextWindow} />}
            onClean={onClean}
            onMessageSelect={onMessageSelect}
            onMessageSend={onMessageSend}
            onRunRetry={onRunRetry}
            onSignIn={onSignIn}
            onSignInCancel={onSignInCancel}
            onSignOut={onSignOut}
          />
        </div>
        {renderSelectedMessageEventDetails()}
      </div>
      <AgentMessageToolsModal
        loading={messageProcessing && !selectedMessage?.tools?.length}
        toolsList={selectedMessage?.tools}
        visible={toolsModalVisible}
        onCancel={onToolsModalCancel}
      />
    </div>
  );
};

export default AgentTestingChat;
